import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  CloudProviderContext,
  getAccountCloudType,
} from "../../../CloudProviderContext";
import {
  useAccounts,
  useUsers,
} from "../../../components/Common/HelperComponents/GetDataFromStore";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import { listAllCatalogFrameworks } from "../../../helpers/auditHub_helper";
import { AWS_ACCOUNT_NUMBER, CREATE_DRAFT_STORAGE_KEY } from "./constants";
import DueDateField from "./DueDateField";
import { formatUserLabel, frameworkLabel } from "./utils";
import {
  createAudit,
  fetchAudits,
  resetCreateAudit,
} from "../../../store/auditHub/auditHubSlice";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  cardShellSx,
  outlinedButtonSx,
  primaryButtonSx,
  searchFieldSx,
  tableShellSx,
} from "./theme";

const asInteger = (value) => {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  return null;
};

const emptyForm = () => ({
  name: "",
  ownerEmail: "",
  defaultAssigneeEmail: "",
  dueDate: "",
  complianceAddonId: null,
  accountNumber: "",
});

const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(CREATE_DRAFT_STORAGE_KEY);
    if (!raw) return emptyForm();
    return { ...emptyForm(), ...JSON.parse(raw) };
  } catch (_e) {
    return emptyForm();
  }
};

const writeDraft = (form) => {
  try {
    sessionStorage.setItem(CREATE_DRAFT_STORAGE_KEY, JSON.stringify(form));
  } catch (_e) {
    /* ignore */
  }
};

const clearDraft = () => {
  try {
    sessionStorage.removeItem(CREATE_DRAFT_STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
};

const clampSx = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
});

const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Box sx={{ ...tableShellSx, ...sx }}>
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: children ? 2 : 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT }}>
              {title}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>{subtitle}</Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const CreateAudit = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const { users, usersLoading } = useUsers();
  const createState = useSelector((s) => s.auditHub?.create) || {};
  const [form, setForm] = useState(readDraft);
  const [errors, setErrors] = useState({});
  const [accountQuery, setAccountQuery] = useState("");
  const [catalogFrameworks, setCatalogFrameworks] = useState([]);
  const [frameworksStatus, setFrameworksStatus] = useState("idle");
  const [frameworksError, setFrameworksError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const skipFirstSave = useRef(true);

  useEffect(() => {
    document.title = "Create New Audit";
    dispatch(resetCreateAudit());
  }, [dispatch]);

  useEffect(() => {
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      writeDraft(form);
    }, 700);
    return () => clearTimeout(handle);
  }, [form]);

  useEffect(() => {
    if (createState.status === "succeeded") {
      const failures = Array.isArray(createState.audit?.scanDispatchFailures)
        ? createState.audit.scanDispatchFailures.filter(Boolean)
        : [];
      clearDraft();
      dispatch(fetchAudits({ page: 1, size: 100 }));
      dispatch(resetCreateAudit());
      history.push({
        pathname: "/audit-hub",
        state: failures.length ? { scanDispatchFailures: failures } : undefined,
      });
    } else if (createState.status === "failed" && createState.error) {
      setSnackbar({ open: true, message: createState.error, severity: "error" });
    }
  }, [createState.status, createState.error, dispatch, history]);

  const userOptions = useMemo(
    () =>
      (users || [])
        .filter((user) => user.email)
        .map((user) => ({
          email: user.email,
          label: formatUserLabel(user),
        })),
    [users]
  );

  const providerAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter((account) => {
      if (account.accountNumber?.includes("INIT00000001")) return false;
      if (account.accountNumber?.includes("TEST")) return false;
      return getAccountCloudType(account) === selectedProvider;
    });
  }, [accounts, selectedProvider]);

  const accountReady = AWS_ACCOUNT_NUMBER.test(form.accountNumber || "");

  useEffect(() => {
    if (!accountReady) {
      setCatalogFrameworks([]);
      setFrameworksStatus("idle");
      setFrameworksError(null);
      return undefined;
    }
    let cancelled = false;
    setCatalogFrameworks([]);
    setFrameworksStatus("loading");
    setFrameworksError(null);
    listAllCatalogFrameworks({ accountNumber: form.accountNumber })
      .then((items) => {
        if (cancelled) return;
        setCatalogFrameworks(items);
        setFrameworksStatus("succeeded");
      })
      .catch((error) => {
        if (cancelled) return;
        setCatalogFrameworks([]);
        setFrameworksStatus("failed");
        setFrameworksError(error?.message || "Unable to load frameworks.");
      });
    return () => {
      cancelled = true;
    };
  }, [accountReady, form.accountNumber]);

  const visibleAccounts = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return providerAccounts;
    return providerAccounts.filter((account) =>
      `${account.accountName || ""}`.toLowerCase().includes(q)
    );
  }, [providerAccounts, accountQuery]);

  useEffect(() => {
    if (!providerAccounts.length) return;
    if (
      form.accountNumber &&
      !providerAccounts.some((a) => a.accountNumber === form.accountNumber)
    ) {
      setForm((prev) => ({ ...prev, accountNumber: "" }));
    }
  }, [form.accountNumber, providerAccounts]);

  useEffect(() => {
    if (form.complianceAddonId == null || frameworksStatus !== "succeeded") return;
    if (!catalogFrameworks.some((fw) => asInteger(fw.complianceAddonId) === form.complianceAddonId)) {
      setForm((prev) => ({ ...prev, complianceAddonId: null }));
    }
  }, [catalogFrameworks, form.complianceAddonId, frameworksStatus]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Audit name is required.";
    if (!form.ownerEmail) next.ownerEmail = "Audit owner is required.";
    if (!form.accountNumber) next.accountNumber = "Select one cloud account.";
    else if (!AWS_ACCOUNT_NUMBER.test(form.accountNumber)) {
      next.accountNumber = "Account number must be 12 digits.";
    }
    if (form.complianceAddonId == null) next.complianceAddonId = "Select one compliance framework.";
    else if (
      !catalogFrameworks.some((fw) => asInteger(fw.complianceAddonId) === form.complianceAddonId)
    ) {
      next.complianceAddonId = "Select one compliance framework.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLaunch = () => {
    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Complete the required fields before launching the audit.",
        severity: "error",
      });
      return;
    }
    const selectedAccount = providerAccounts.find(
      (account) => account.accountNumber === form.accountNumber
    );
    const regions = Array.isArray(selectedAccount?.regions)
      ? selectedAccount.regions
          .filter((region) => typeof region === "string" && region.trim())
          .slice(0, 32)
      : [];
    const account = {
      accountNumber: form.accountNumber,
      complianceAddonIds: [form.complianceAddonId],
    };
    if (regions.length) account.regions = regions;
    const body = {
      name: form.name.trim(),
      ownerEmail: form.ownerEmail,
      accounts: [account],
    };
    if (form.defaultAssigneeEmail) body.defaultAssigneeEmail = form.defaultAssigneeEmail;
    if (form.dueDate) body.dueDate = form.dueDate;
    dispatch(createAudit(body));
  };

  const ownerValue = userOptions.find((u) => u.email === form.ownerEmail) || null;
  const assigneeValue = userOptions.find((u) => u.email === form.defaultAssigneeEmail) || null;
  const launching = createState.status === "loading";

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED }}>
                <Link
                  component="button"
                  underline="hover"
                  color="inherit"
                  onClick={() => history.push("/audit-hub")}
                  sx={{ fontSize: 13 }}
                >
                  Audits
                </Link>
                <Typography sx={{ fontSize: 13, color: AH_TEXT }}>Create New Audit</Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Create New Audit
              </Typography>
              <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                Configure one framework on one 12-digit account and assign ownership.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 5fr) minmax(0, 7fr)" },
                gridTemplateAreas: {
                  xs: `"timeline" "meta" "account" "scope"`,
                  lg: `"timeline account" "meta scope"`,
                },
                gap: 2,
                alignItems: "start",
              }}
            >
                <SectionCard
                  title="Metadata & Governance"
                  subtitle="Name, owner, and default assignee (applied to manual controls at launch)."
                  sx={{ gridArea: "meta", alignSelf: "stretch", minWidth: 0 }}
                >
                  <TextField
                    label="Audit Name"
                    required
                    fullWidth
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    placeholder="Q3 2026 Security Compliance Audit"
                    inputProps={{ maxLength: 200 }}
                    sx={{ ...fieldSx, mb: 2 }}
                  />
                  <Autocomplete
                    options={userOptions}
                    loading={usersLoading}
                    value={ownerValue}
                    onChange={(_e, option) => patch({ ownerEmail: option?.email || "" })}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Audit Owner"
                        required
                        error={Boolean(errors.ownerEmail)}
                        helperText={errors.ownerEmail}
                        sx={{ ...fieldSx, mb: 2 }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={userOptions}
                    loading={usersLoading}
                    value={assigneeValue}
                    onChange={(_e, option) => patch({ defaultAssigneeEmail: option?.email || "" })}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Default Assignee"
                        helperText="Optional. Applied to every manual control at launch."
                        sx={fieldSx}
                      />
                    )}
                  />
                </SectionCard>

                <SectionCard
                  title="Timeline"
                  subtitle="Optional target completion date. Start time is set when the audit is launched."
                  sx={{ gridArea: "timeline", alignSelf: "stretch", minWidth: 0 }}
                >
                  <DueDateField value={form.dueDate} onChange={(dueDate) => patch({ dueDate })} />
                </SectionCard>
                <SectionCard
                  title="Scope & Framework"
                  subtitle="Frameworks this account can be audited on."
                  sx={{ gridArea: "scope", alignSelf: "stretch", minWidth: 0 }}
                  action={
                    <Typography sx={{ fontSize: 12, color: errors.complianceAddonId ? "#dc2626" : AH_MUTED, whiteSpace: "nowrap", pt: 0.25 }}>
                      {form.complianceAddonId != null ? "1 selected" : "Select one"}
                    </Typography>
                  }
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.25,
                      alignItems: "stretch",
                      maxHeight: 246,
                      overflowY: "auto",
                      pr: 0.5,
                      scrollbarWidth: "thin",
                      overscrollBehavior: "contain",
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.18)",
                        borderRadius: 3,
                      },
                    }}
                  >
                    {!accountReady ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED, gridColumn: "1 / -1" }}>
                        Select a 12-digit account to load its frameworks.
                      </Typography>
                    ) : frameworksStatus === "loading" ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4, gridColumn: "1 / -1" }}>
                        <CircularProgress size={22} />
                      </Box>
                    ) : frameworksStatus === "failed" ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: "#dc2626", gridColumn: "1 / -1" }}>
                        {frameworksError}
                      </Typography>
                    ) : catalogFrameworks.length === 0 ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED, gridColumn: "1 / -1" }}>
                        No frameworks are available for this account.
                      </Typography>
                    ) : (
                      catalogFrameworks.map((fw) => {
                        const addonId = asInteger(fw.complianceAddonId);
                        const selected = form.complianceAddonId === addonId;
                        const label = frameworkLabel(fw);
                        const icon = getComplianceIcon(label);
                        const manualCount = fw.manualControlCount;
                        return (
                          <Box
                            key={fw.complianceAddonId}
                            component="button"
                            type="button"
                            disabled={addonId == null}
                            onClick={() => {
                              if (addonId == null) return;
                              patch({ complianceAddonId: addonId });
                              setErrors((prev) => ({ ...prev, complianceAddonId: undefined }));
                            }}
                            sx={{
                              ...cardShellSx,
                              minHeight: 112,
                              height: 112,
                              p: 1.5,
                              textAlign: "left",
                              cursor: "pointer",
                              border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
                              backgroundColor: selected ? "#fff4eb" : AH_WHITE,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.25,
                              fontFamily: "inherit",
                              "&:hover": { borderColor: selected ? AH_ACCENT : "#cbd5e1" },
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {icon ? (
                                <Box component="img" src={icon} alt="" sx={{ maxHeight: 24, maxWidth: 28 }} />
                              ) : (
                                <CheckCircleOutlineIcon sx={{ fontSize: 20, color: AH_MUTED }} />
                              )}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Tooltip title={label} placement="top">
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT, lineHeight: 1.35, ...clampSx(2) }}>
                                  {label}
                                </Typography>
                              </Tooltip>
                              <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.5, lineHeight: 1.4, ...clampSx(2) }}>
                                {Number.isInteger(manualCount)
                                  ? `${manualCount} manual control${manualCount === 1 ? "" : "s"}`
                                  : "—"}
                              </Typography>
                            </Box>
                            {selected ? (
                              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: AH_ACCENT, flexShrink: 0 }} />
                            ) : null}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  {errors.complianceAddonId ? (
                    <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.complianceAddonId}</Typography>
                  ) : null}
                </SectionCard>

                <SectionCard
                  title={selectedProvider === "AZURE" ? "Azure Subscription" : "Cloud Account"}
                  subtitle="One 12-digit account. Adding it starts that account's scan."
                  sx={{ gridArea: "account", alignSelf: "stretch", minWidth: 0 }}
                  action={
                    <Typography sx={{ fontSize: 12, color: errors.accountNumber ? "#dc2626" : AH_MUTED, whiteSpace: "nowrap", pt: 0.25 }}>
                      {form.accountNumber ? "1 selected" : `${providerAccounts.length} available`}
                    </Typography>
                  }
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={selectedProvider === "AZURE" ? "Search subscriptions..." : "Search accounts..."}
                    value={accountQuery}
                    onChange={(e) => setAccountQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...searchFieldSx, mb: 1.25 }}
                  />
                  <Box
                    sx={{
                      ...cardShellSx,
                      maxHeight: 220,
                      overflowY: "auto",
                      borderColor: errors.accountNumber ? "#dc2626" : AH_BORDER,
                    }}
                  >
                    {visibleAccounts.length === 0 ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED }}>
                        No {selectedProvider === "AZURE" ? "subscriptions" : "accounts"} match this search.
                      </Typography>
                    ) : (
                      visibleAccounts.map((account) => {
                        const selected = form.accountNumber === account.accountNumber;
                        return (
                          <Box
                            key={account.accountCode || account.accountNumber}
                            component="button"
                            type="button"
                            onClick={() => {
                              patch({ accountNumber: account.accountNumber });
                              setErrors((prev) => ({ ...prev, accountNumber: undefined }));
                            }}
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              minHeight: 44,
                              border: 0,
                              borderBottom: `1px solid ${AH_BORDER}`,
                              backgroundColor: selected ? "#fff4eb" : AH_WHITE,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              textAlign: "left",
                              "&:last-of-type": { borderBottom: 0 },
                              "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: `2px solid ${selected ? AH_ACCENT : AH_BORDER}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {selected ? (
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: AH_ACCENT }} />
                              ) : null}
                            </Box>
                            <CloudProviderMark
                              provider={selectedProvider}
                              sx={{
                                mr: 0,
                                "& .cloud-provider-mark--aws": { height: 14, width: 26 },
                                "& .cloud-provider-mark--azure": { height: 16, width: 16 },
                              }}
                            />
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: selected ? 700 : 500, color: AH_TEXT }}>
                              {account.accountName || account.accountCode}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  {errors.accountNumber ? (
                    <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.accountNumber}</Typography>
                  ) : null}
                </SectionCard>
            </Box>

            <Box
              sx={{
                ...tableShellSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                position: { md: "sticky" },
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Button variant="outlined" onClick={() => history.push("/audit-hub")} sx={outlinedButtonSx}>
                Cancel
              </Button>
              <Button
                startIcon={<RocketLaunchOutlinedIcon />}
                onClick={handleLaunch}
                disabled={launching || frameworksStatus === "loading"}
                sx={primaryButtonSx}
              >
                {launching ? "Launching..." : "Launch Audit"}
              </Button>
            </Box>
          </Box>
        </Container>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuditHubThemeProvider>
  );
};

export default CreateAudit;
